import { Assets, Rectangle, Texture } from 'pixi.js';
import type { MathConfig, ResolvedTheme, SymbolId } from '../types';
import { sheetFrameRects } from './sheet';

/** Loads and caches every image a theme references. SVGs are rasterised at >= 2x the largest symbol size. */
export class TextureStore {
  private symbols = new Map<SymbolId, Texture>();
  private blurs = new Map<SymbolId, Texture>();
  private frameSets = new Map<SymbolId, Texture[]>();
  private frameFps = new Map<SymbolId, number>();
  private images = new Map<string, Texture>();
  private loadedUrls = new Set<string>();
  private sheetsLoading: Promise<void> | null = null;
  private sheetCache = new Map<string, Promise<Texture[]>>();
  private winFrame: Texture[] | null = null;
  private winFrameFpsValue = 16;

  /** Load a sprite sheet once and slice it into per-frame textures (shared between symbols that use the same file). */
  private loadSheet(spec: { image: string; cell: number; frames: number }): Promise<Texture[]> {
    let p = this.sheetCache.get(spec.image);
    if (!p) {
      p = (Assets.load(spec.image) as Promise<Texture>).then((tex) => {
        this.loadedUrls.add(spec.image);
        return sheetFrameRects(spec.cell, spec.frames, tex.width).map((r) => new Texture({ source: tex.source, frame: new Rectangle(r.x, r.y, r.w, r.h) }));
      });
      this.sheetCache.set(spec.image, p);
    }
    return p;
  }

  async load(theme: ResolvedTheme, math: MathConfig, onProgress?: (p: number) => void): Promise<void> {
    const maxSymbol = Math.max(
      theme.layout.portrait.reels.symbolWidth,
      theme.layout.portrait.reels.symbolHeight,
      theme.layout.landscape.reels.symbolWidth,
      theme.layout.landscape.reels.symbolHeight,
    );
    // SVG rasterisation: our symbols are 200x200 viewBoxes; scale so the bitmap is >= 2x the largest cell.
    const svgResolution = Math.max(1, Math.ceil((maxSymbol * 2) / 200));

    const jobs: Array<{ url: string; assign: (t: Texture) => void }> = [];
    for (const s of math.symbols) {
      const a = theme.assets.symbols[s.id];
      if (!a?.image) throw new Error(`Theme "${theme.id}" has no image for symbol ${s.id}`);
      jobs.push({ url: a.image, assign: (t) => this.symbols.set(s.id, t) });
      if (a.blurImage) jobs.push({ url: a.blurImage, assign: (t) => this.blurs.set(s.id, t) });
      if (a.frames?.length) {
        const frames: Texture[] = new Array(a.frames.length);
        a.frames.forEach((url, i) => jobs.push({ url, assign: (t) => (frames[i] = t) }));
        this.frameSets.set(s.id, frames);
      }
    }
    const optional = [theme.assets.background?.base, theme.assets.background?.freeSpins, theme.assets.reelFrame, theme.assets.logo, theme.assets.coin, theme.assets.outcomeCard?.win, theme.assets.brandSignature];
    for (const url of optional) if (url) jobs.push({ url, assign: (t) => this.images.set(url, t) });

    let done = 0;
    const total = jobs.length;
    await Promise.all(
      jobs.map(async (job) => {
        try {
          const tex = await this.loadOne(job.url, svgResolution);
          job.assign(tex);
        } catch (err) {
          if (this.isRequired(job.url, theme, math)) throw err;
          console.warn(`[simple-slot] optional asset failed to load: ${job.url}`);
        }
        done++;
        onProgress?.(done / total);
      }),
    );
    if (total === 0) onProgress?.(1);
  }

  /**
   * Win sprite sheets are optional and heavy (≈0.3–0.5 MB per symbol), so they load after the game is ready
   * instead of holding the loading screen. Symbols whose sheet has not arrived yet fall back to the 'float' effect.
   */
  loadWinSheets(theme: ResolvedTheme, math: MathConfig): Promise<void> {
    if (this.sheetsLoading) return this.sheetsLoading;
    const jobs: Promise<void>[] = math.symbols.map(async (s) => {
      const sheet = theme.assets.symbols[s.id]?.winSheet;
      if (!sheet || this.frameSets.has(s.id)) return;
      try {
        this.frameSets.set(s.id, await this.loadSheet(sheet));
        this.frameFps.set(s.id, sheet.fps ?? theme.assets.symbols[s.id]?.fps ?? 12);
      } catch {
        console.warn(`[simple-slot] win sheet failed to load: ${sheet.image}`);
      }
    });
    const frame = theme.assets.winFrame;
    if (frame) {
      jobs.push(
        this.loadSheet(frame)
          .then((frames) => {
            this.winFrame = frames;
            this.winFrameFpsValue = frame.fps ?? 16;
          })
          .catch(() => console.warn(`[simple-slot] win frame sheet failed to load: ${frame.image}`)),
      );
    }
    this.sheetsLoading = Promise.all(jobs).then(() => undefined);
    return this.sheetsLoading;
  }

  private isRequired(url: string, theme: ResolvedTheme, math: MathConfig): boolean {
    return math.symbols.some((s) => theme.assets.symbols[s.id]?.image === url);
  }

  private async loadOne(url: string, svgResolution: number): Promise<Texture> {
    const isSvg = /\.svg(\?|#|$)/i.test(url) || url.startsWith('data:image/svg');
    const tex = (await Assets.load(isSvg ? { src: url, data: { resolution: svgResolution, parseAsGraphicsContext: false } } : url)) as Texture;
    this.loadedUrls.add(url);
    return tex;
  }

  symbol(id: SymbolId): Texture {
    return this.symbols.get(id) ?? Texture.WHITE;
  }

  symbolBlur(id: SymbolId): Texture | null {
    return this.blurs.get(id) ?? null;
  }

  frames(id: SymbolId): Texture[] | null {
    const f = this.frameSets.get(id);
    return f && f.length && f.every(Boolean) ? f : null;
  }

  /** True once every symbol that declares a win sheet has its frames: the effect switches for all symbols at once. */
  winSheetsReady(theme: ResolvedTheme, math: MathConfig): boolean {
    return math.symbols.every((s) => !theme.assets.symbols[s.id]?.winSheet || this.frameSets.has(s.id));
  }

  /** Frames of the shared winning-cell frame, once its sheet has loaded. */
  winFrameFrames(): Texture[] | null {
    return this.winFrame;
  }

  winFrameFps(): number {
    return this.winFrameFpsValue;
  }

  /** Playback rate of a symbol's frame set (sheet fps, else the theme's `fps`, else 12). */
  framesFps(id: SymbolId, fallback = 12): number {
    return this.frameFps.get(id) ?? fallback;
  }

  image(url: string): Texture | null {
    return this.images.get(url) ?? null;
  }

  /** URLs this store loaded (for keep-sets when swapping stores). */
  get urls(): Set<string> {
    return new Set(this.loadedUrls);
  }

  /** Unload textures, except those in `keep` (still used by a newer store that loaded the same URLs). */
  destroy(keep?: Set<string>): void {
    for (const url of this.loadedUrls) if (!keep?.has(url)) void Assets.unload(url).catch(() => {});
    this.loadedUrls.clear();
    this.symbols.clear();
    this.blurs.clear();
    this.frameSets.clear();
    this.frameFps.clear();
    this.sheetCache.clear();
    this.winFrame = null;
    this.images.clear();
  }
}
